import { useState, useCallback, useMemo } from "react";
import { CopyFormattedButton } from "@/components/copy-formatted-button";
import { Button } from "@ndd/ui";
import { formatScorecardSectionCopy } from "@/lib/analysis-copy-format";
import { ScorecardItemRow } from "./scorecard-item-row";
import { cn } from "@/lib/utils";
import {
  filterVisibleScorecardItems,
  type ResolvedScorecardItem,
} from "./scorecard-section-logic";

export { filterVisibleScorecardItems };
export type { ResolvedScorecardItem };

interface ScorecardSectionProps {
  title: string;
  items: ResolvedScorecardItem[];
  className?: string;
}

export function ScorecardSection({
  title,
  items,
  className,
}: ScorecardSectionProps) {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [areMeetsVisible, setAreMeetsVisible] = useState(false);

  const visibleItems = useMemo(
    () => filterVisibleScorecardItems(items, areMeetsVisible),
    [items, areMeetsVisible],
  );

  const meetsCount = useMemo(
    () => items.filter((item) => item.rating === "meets").length,
    [items],
  );

  const hiddenMeetsCount = areMeetsVisible ? 0 : meetsCount;

  const allExpanded = useMemo(
    () => visibleItems.length > 0 && visibleItems.every((item) => openIds.has(item.id)),
    [visibleItems, openIds],
  );

  function handleToggleItem(id: string, open: boolean) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (open) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  const handleToggleAll = useCallback(() => {
    if (allExpanded) {
      setOpenIds(new Set());
    } else {
      setOpenIds(new Set(visibleItems.map((item) => item.id)));
    }
  }, [allExpanded, visibleItems]);

  return (
    <div
      className={cn(
        "rounded-xl border border-sand-200 bg-card p-5",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
            {title}
          </h3>
          {visibleItems.length > 0 && (
            <CopyFormattedButton
              format={formatScorecardSectionCopy(title, visibleItems)}
            >
              Copy section
            </CopyFormattedButton>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-sand-400">
            {visibleItems.length} of {items.length} items
          </span>
          {visibleItems.length > 0 && (
            <button
              type="button"
              onClick={handleToggleAll}
              className="text-xs font-semibold text-interactive hover:text-interactive-hover"
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </button>
          )}
        </div>
      </div>

      {items.length > 0 && meetsCount > 0 && (
        <div className="mt-4 flex flex-col gap-2 rounded-lg border border-sand-200 bg-sand-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            shape="pill"
            aria-pressed={areMeetsVisible}
            onClick={() => setAreMeetsVisible((visible) => !visible)}
          >
            {areMeetsVisible ? "Hide Meets" : "Show Meets"}
          </Button>
          <span className="text-xs text-sand-500">
            {hiddenMeetsCount > 0
              ? `${hiddenMeetsCount} Meets ${hiddenMeetsCount === 1 ? "item is" : "items are"} hidden`
              : "Meets items are visible"}
          </span>
        </div>
      )}

      {visibleItems.length > 0 ? (
        <div className="mt-4 flex flex-col gap-3">
          {visibleItems.map((item) => (
            <ScorecardItemRow
              key={item.id}
              rating={item.rating}
              label={item.label}
              summary={item.summary}
              evidence={item.evidence}
              what_to_try={item.what_to_try}
              why_it_matters={item.why_it_matters}
              copy_paste_reinforcement={item.copy_paste_reinforcement}
              open={openIds.has(item.id)}
              onOpenChange={(open) => handleToggleItem(item.id, open)}
            />
          ))}
        </div>
      ) : items.length > 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-sand-200 bg-sand-50 px-4 py-5 text-sm text-sand-600">
          All items in this section are rated Meets. Use Show Meets to view them.
        </div>
      ) : null}
    </div>
  );
}
